import React, { useEffect, useMemo } from "react";
import "./style.scss";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Pie } from "react-chartjs-2";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { usePainelPublico } from "../../../hooks/usePainelPublico";
import { actionGetDashboardGeralPublico } from "../../../features/(ApiKey)/Mapa/get/slices";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { actionGetAlertasProperty } from "../../../features/alertas/getAll-alertas/slices";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

const CORES_PIZZA = [
  "#e74c3c",
  "#3498db",
  "#2ecc71",
  "#f39c12",
  "#9b59b6",
  "#1abc9c",
  "#34495e",
  "#e84393",
  "#7f8c8d",
  "#2980b9",
];

const VisaoExecutiva = () => {
  const dispatch = useAppDispatch();

  const { apiKey, status: statusPainel, data: painelData } = usePainelPublico();

  const dashboard = useAppSelector((state) => state.getDashboardGeralPublico);
  const { data: metrics, status: statusProperty } = useAppSelector(
    (state) => state.getAlertasProperty,
  );

  const idCliente = painelData?.idCliente as number;

  useEffect(() => {
    if (statusPainel !== "ready" || !apiKey) return;

    const fetchDadosDashboard = () => {
      dispatch(actionGetDashboardGeralPublico(apiKey));

      if (idCliente !== undefined && idCliente !== null) {
        dispatch(
          actionGetAlertasProperty({
            idLoja: 0,
            idCliente: idCliente,
            apiKey: apiKey,
          }),
        );
      }
    };

    fetchDadosDashboard();

    const interval = setInterval(fetchDadosDashboard, 60000);
    return () => clearInterval(interval);
  }, [statusPainel, apiKey, idCliente, dispatch]);

  const alertasCriticosReal = metrics?.paraTratamento ?? 0;
  const alertasAtrasadosReal = metrics?.alertasAtrasados ?? 0;

  const calcularDisponibilidadeEstadosReal = useMemo(() => {
    const listaEstados =
      dashboard?.data?.mapaBrasil || (dashboard?.data as any)?.mapaBrasil;

    if (
      !listaEstados ||
      !Array.isArray(listaEstados) ||
      listaEstados.length === 0
    ) {
      return "100%";
    }

    const totalEstados = listaEstados.length;
    const estadosSaudaveis = listaEstados.filter(
      (estado) => String(estado.status).toUpperCase() === "OK",
    ).length;

    const percentualDisponibilidade = Math.round(
      (estadosSaudaveis / totalEstados) * 100,
    );

    return `${percentualDisponibilidade}%`;
  }, [dashboard?.data]);
  const taxaResolucao30d = useMemo(() => {
    const tratadas = metrics?.tratadas30d ?? 0;
    const abertas = metrics?.ocorrencias30d ?? 0;
    const total = tratadas + abertas;

    if (total === 0) return "—";

    const percentual = Math.round((tratadas / total) * 100);
    return `${percentual}%`;
  }, [metrics?.tratadas30d, metrics?.ocorrencias30d]);

  const formatarTempoMedio = (minutosTotais: number | undefined): string => {
    if (!minutosTotais) return "0 min";
    if (minutosTotais < 60) return `${minutosTotais} min`;

    const horas = Math.floor(minutosTotais / 60);
    const minutosRestantes = minutosTotais % 60;

    if (minutosRestantes === 0) return `${horas}h`;
    return `${horas}h ${minutosRestantes}min`;
  };

  const mttaFormatado = formatarTempoMedio(metrics?.mttaMinutos);
  const mttrFormatado = formatarTempoMedio(metrics?.mttrMinutos);

  const dadosMttaGauge = useMemo(() => {
    const minutos = metrics?.mttaMinutos ?? 0;
    if (minutos === 0) return [0, 100];

    const tetoVisual = minutos > 1440 ? minutos * 1.3 : 1440;
    return [minutos, tetoVisual - minutos];
  }, [metrics?.mttaMinutos]);

  const valorBrutoImportancia = useMemo(() => {
    return Number(metrics?.importanciaSegurada) || 0;
  }, [metrics?.importanciaSegurada]);

  const valorFormatadoImportancia = useMemo(() => {
    return valorBrutoImportancia.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }, [valorBrutoImportancia]);

  const dadosImportanciaGauge = useMemo(() => {
    const maxEscala6Bi = 6000000000;
    const valorAtual = Math.min(valorBrutoImportancia, maxEscala6Bi);
    return [valorAtual, maxEscala6Bi - valorAtual];
  }, [valorBrutoImportancia]);

  const alertasPorDiaSemana = metrics?.alertasPorDiaSemana ?? [];
  const alertasPorHora = metrics?.alertasPorHora ?? [];
  const alertasPorEquipamento = metrics?.alertasPorEquipamento ?? [];

  const dataDiaSemana = useMemo(
    () => ({
      labels: alertasPorDiaSemana.map((d: any) => d.diaSemana),
      datasets: [
        {
          data: alertasPorDiaSemana.map((d: any) => d.quantidade),
          backgroundColor: "#3498db",
          borderRadius: 4,
        },
      ],
    }),
    [alertasPorDiaSemana],
  );

  const dataHora = useMemo(
    () => ({
      labels: alertasPorHora.map((h: any) => h.periodo),
      datasets: [
        {
          data: alertasPorHora.map((h: any) => h.quantidade),
          backgroundColor: "#e67e22",
          borderRadius: 4,
        },
      ],
    }),
    [alertasPorHora],
  );

  const dataEquipamento = useMemo(
    () => ({
      labels: alertasPorEquipamento.map((e: any) => e.nomeEquipamento),
      datasets: [
        {
          data: alertasPorEquipamento.map((e: any) => e.quantidade),
          backgroundColor: CORES_PIZZA,
          borderWidth: 0,
        },
      ],
    }),
    [alertasPorEquipamento],
  );
  const optionsBarra = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false } },
      y: { border: { dash: [5, 5] }, min: 0 },
    },
  };

  const optionsPizza = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right" as const,
        labels: { boxWidth: 12, font: { size: 11 } },
      },
    },
  };

  const generarOptionsGauge = () => ({
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2,
    rotation: -90,
    circumference: 180,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    cutout: "75%",
  });

  const gerarDataGauge = (dadosGauge: number[], corPrincipal: string) => ({
    datasets: [
      {
        data: dadosGauge,
        backgroundColor: [corPrincipal, "#e2e8f0"],
        borderWidth: 0,
      },
    ],
  });

  return (
    <div className="visao-executiva-container">
      <header className="main-header-executivo">
        <div>
          {painelData &&
            painelData.logos.map((logoUrl: string, index: number) => (
              <img
                key={index}
                src={logoUrl}
                style={{ height: "50px", marginRight: "20px" }}
                alt={`Logo ${index + 1}`}
              />
            ))}
        </div>
        <div className="title-side">
          <h2>Visão Executiva - Dashboard Sompo</h2>
          <p>Resultados acumulados e impacto financeiro</p>
        </div>
      </header>

      <main className="painel-moldura-sompo">
        <section className="kpi-cards-row">
          <div className="card-kpi" style={{ borderTop: "4px solid #2ecc71" }}>
            <h4>Taxa de Resolução (30d)</h4>
            <div className="valor-container">
              <strong>{taxaResolucao30d}</strong>
            </div>
          </div>

          <div className="card-kpi" style={{ borderTop: "4px solid #f39c12" }}>
            <h4>MTTR Médio</h4>
            <div className="valor-container">
              <strong>{mttrFormatado}</strong>
            </div>
          </div>

          <div className="card-kpi" style={{ borderTop: "4px solid #2ecc71" }}>
            <h4>Disponibilidade</h4>
            <div className="valor-container">
              <strong>{calcularDisponibilidadeEstadosReal}</strong>
            </div>
          </div>

          <div className="card-kpi" style={{ borderTop: "4px solid #e74c3c" }}>
            <h4>Alertas Críticos (24h)</h4>
            <div className="valor-container">
              <strong>{alertasCriticosReal}</strong>
            </div>
          </div>

          <div className="card-kpi" style={{ borderTop: "4px solid #e74c3c" }}>
            <h4>Alertas em Atraso</h4>
            <div className="valor-container">
              <strong>{alertasAtrasadosReal}</strong>
            </div>
          </div>
        </section>

        <section className="grid-tendencia-eficiencia">
          <article className="card-graph-trend">
            <header>
              <h4>Alertas por Dia da Semana (30 dias)</h4>
            </header>
            <div className="container-grafico-real">
              <Bar options={optionsBarra} data={dataDiaSemana} />
            </div>
          </article>

          {/* ─── Gauges lado a lado: Tempo de Atendimento + Importância Segurada ─── */}
          <article className="grid-eficiencia">
            <div className="gauges-row">
              <div className="gauge-item">
                <h2>Tempo de Atendimento</h2>
                <div className="card-gauge card-gauge--single">
                  <div className="container-gauge-real">
                    <div className="gauge-canvas-wrapper">
                      <Doughnut
                        options={generarOptionsGauge()}
                        data={gerarDataGauge(dadosMttaGauge, "#2ecc71")}
                      />
                    </div>
                    <div className="valor-central-gauge">
                      <span>{mttaFormatado}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="gauge-item">
                <h2>Importância Segurada</h2>
                <div className="card-gauge card-gauge--single">
                  <div className="container-gauge-real">
                    <div className="gauge-canvas-wrapper">
                      <Doughnut
                        options={generarOptionsGauge()}
                        data={gerarDataGauge(dadosImportanciaGauge, "#ff9f43")}
                      />
                    </div>
                    <div className="valor-central-gauge">
                      <span>{valorFormatadoImportancia}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </section>

        <section className="grid-tendencia-eficiencia">
          <article className="card-graph-trend">
            <header>
              <h4>Alertas por Horário (30 dias)</h4>
            </header>
            <div className="container-grafico-real">
              <Bar options={optionsBarra} data={dataHora} />
            </div>
          </article>

          <article className="card-graph-trend">
            <header>
              <h4>Top Equipamentos com Falha (30 dias)</h4>
            </header>
            <div className="container-grafico-real">
              {alertasPorEquipamento.length > 0 ? (
                <Pie options={optionsPizza} data={dataEquipamento} />
              ) : (
                <p className="paragraph paragraph--sm">
                  Sem dados de equipamentos no período.
                </p>
              )}
            </div>
          </article>
        </section>
      </main>
    </div>
  );
};

export default VisaoExecutiva;